import type { SchemaFieldDefinitionResponse } from '@erp/rpc';

function typedLeafPlaceholder(fieldType: string): string {
  switch (fieldType) {
    case 'string':
    case 'text':
    case 'url':
    case 'enum':
      return '<string>';
    case 'integer':
    case 'float':
    case 'currency':
    case 'unit':
      return '<number>';
    case 'boolean':
      return '<boolean>';
    case 'date':
    case 'datetime':
    case 'time':
      return '<date>';
    case 'json':
      return '<json>';
    case 'range':
      return '<range>';
    case 'file':
    case 'image':
      return '<file>';
    case 'geospatial':
      return '<geospatial>';
    default:
      return `<${fieldType}>`;
  }
}

function buildTypedValueForField(
  field: SchemaFieldDefinitionResponse,
): unknown {
  if (field.fieldType === 'object') {
    const obj: Record<string, unknown> = {};
    for (const child of field.children ?? []) {
      obj[child.fieldName] = buildTypedValueForField(child);
    }
    return obj;
  }
  if (field.fieldType === 'array') {
    const child = field.children?.[0];
    return child ? [buildTypedValueForField(child)] : ['<item>'];
  }
  return typedLeafPlaceholder(field.fieldType);
}

/**
 * Shape-only preview: values are type placeholders (`<string>`, `<number>`, …)
 * for documentation (e.g. hover full JSON). Only **root** fields (`!parentFieldId`)
 * become top-level JSON keys, matching the HTTP trigger contract.
 */
function rootSchemaFields(
  fields: SchemaFieldDefinitionResponse[],
): SchemaFieldDefinitionResponse[] {
  return fields.filter((f) => !f.parentFieldId);
}

export function buildHttpTriggerTypedPreviewFromFields(
  fields: SchemaFieldDefinitionResponse[],
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const field of rootSchemaFields(fields)) {
    out[field.fieldName] = buildTypedValueForField(field);
  }
  return out;
}

function sampleLeafValue(field: SchemaFieldDefinitionResponse): unknown {
  switch (field.fieldType) {
    case 'string':
    case 'text':
    case 'url':
    case 'file':
    case 'image':
      return 'example';
    case 'enum': {
      const first = field.enumValues?.[0];
      return typeof first === 'string' && first.length > 0 ? first : 'example';
    }
    case 'integer':
      return 0;
    case 'float':
    case 'currency':
    case 'unit':
      return 0;
    case 'boolean':
      return false;
    case 'date':
      return '2020-01-01';
    case 'time':
      return '12:00';
    case 'datetime':
      return '2020-01-15T12:00:00.000Z';
    case 'json':
      return {};
    case 'range':
      return { min: 0, max: 1 };
    case 'geospatial':
      return { lat: 0, lng: 0 };
    default:
      return null;
  }
}

function buildSampleValueForField(
  field: SchemaFieldDefinitionResponse,
): unknown {
  if (field.fieldType === 'object') {
    const obj: Record<string, unknown> = {};
    for (const child of field.children ?? []) {
      obj[child.fieldName] = buildSampleValueForField(child);
    }
    return obj;
  }
  if (field.fieldType === 'array') {
    const child = field.children?.[0];
    return child ? [buildSampleValueForField(child)] : [];
  }
  return sampleLeafValue(field);
}

/**
 * Example JSON that should pass `generateZodSchema(...).strict()` for the
 * same field tree (curl, token reveal “Try it”, copy buttons).
 */
export function buildHttpTriggerSamplePayloadFromFields(
  fields: SchemaFieldDefinitionResponse[],
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const field of rootSchemaFields(fields)) {
    out[field.fieldName] = buildSampleValueForField(field);
  }
  return out;
}

/**
 * Pretty-printed JSON for UI previews (e.g. schema dropdown rows).
 */
export function formatTypedPayloadPreviewJson(
  fields: SchemaFieldDefinitionResponse[],
  indent = 2,
): string {
  return JSON.stringify(
    buildHttpTriggerTypedPreviewFromFields(fields),
    null,
    indent,
  );
}

/** One-line JSON for tight list rows; truncates with an ellipsis. */
export function compactPayloadPreview(
  payload: Record<string, unknown>,
  maxLen = 96,
): string {
  const s = JSON.stringify(payload);
  if (s.length <= maxLen) return s;
  return `${s.slice(0, maxLen - 1)}…`;
}
